/**
 * /services/international/:slug &mdash; Profile + inquire dialog for a
 * single international service pro.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, ArrowLeftIcon as ArrowLeft, DogIcon as Dog, WarningIcon as Warning } from "@phosphor-icons/react";

export default function InternationalServiceProProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/services/international/${slug}`)
      .then((r) => setPro(r.data))
      .catch(() => setPro(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <AppShell><div className="max-w-3xl mx-auto px-4 py-10"><Skeleton className="h-44 rounded-xl" /></div></AppShell>;
  }
  if (!pro) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <h1 className="font-spectral text-3xl">Pro not found</h1>
          <Button variant="outline" className="rounded-full mt-4"
            onClick={() => navigate("/services/international")}>
            ← Back to directory
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div
        className="max-w-3xl mx-auto px-4 py-8"
        data-testid={`intl-svc-profile-${pro.slug}`}
      >
        <button
          onClick={() => navigate("/services/international")}
          className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> All pros
        </button>

        <div className="bg-white border border-[#E5D08A] rounded-xl p-6 sm:p-8">
          <div className="flex items-start gap-5">
            {pro.photo_url ? (
              <img src={pro.photo_url} alt={pro.display_name} className="w-20 h-20 rounded-full object-cover border border-[#F0EAD6]" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] flex items-center justify-center">
                <Dog size={40} weight="duotone" className="text-[#8A6A00]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-spectral text-3xl tracking-tight">{pro.display_name}</h1>
              <div className="text-[13px] text-[#6B6B6B] mt-1">
                {(pro.service_kind || "").toUpperCase()} &middot;{" "}
                <strong>{pro.home_country}</strong>
                {pro.years_in_business > 0 && <> &middot; {pro.years_in_business} yrs in business</>}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-[#8A6A00] font-semibold bg-[#FBF7EC] border border-[#E5D08A] px-2 py-0.5 rounded-full">
                <ShieldCheck size={11} weight="duotone" /> Armstrong Verified
              </div>
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="rounded-full bg-crimson hover:bg-ink text-white text-[12px] h-10 px-5"
              data-testid="intl-svc-cta-quote"
            >
              Request quote
            </Button>
          </div>

          {pro.bio && <p className="mt-6 text-[14px] leading-relaxed text-ink">{pro.bio}</p>}

          <div className="mt-6 border-t border-[#F0EAD6] pt-5">
            <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">Cities served</div>
            <div className="flex flex-wrap gap-1.5">
              {(pro.cities_served || []).map((c) => (
                <span key={c} className="text-[12px] px-2.5 py-1 rounded-full bg-[#FBF7EC] border border-[#F0EAD6]">{c}</span>
              ))}
            </div>
          </div>

          {(pro.certifications || []).length > 0 && (
            <div className="mt-6 border-t border-[#F0EAD6] pt-5">
              <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">Certifications</div>
              <div className="flex flex-wrap gap-1.5">
                {pro.certifications.map((c) => (
                  <span key={c} className="text-[12px] px-2.5 py-1 rounded-full bg-white border border-[#E5E5E5]">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4 text-[11px] text-[#5C5C5C] leading-relaxed flex gap-3">
          <Warning size={20} weight="duotone" className="text-[#8A6A00] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Armstrong is a venue.</strong> We introduce you to this independent pro.
            We do not provide or verify the actual services. Verify credentials and contract directly with the pro.
          </div>
        </div>
      </div>

      <QuoteDialog open={open} onOpenChange={setOpen} slug={slug} pro={pro} />
    </AppShell>
  );
}

function QuoteDialog({ open, onOpenChange, slug, pro }) {
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    buyer_city: "", buyer_country: "",
    requested_date: "", notes: "",
  });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!ack) {
      toast.error("Please acknowledge the venue disclaimer.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/services/international/${slug}/inquire`, {
        ...form, venue_acknowledged: true,
      });
      if (r.data?.requires_buyer_payment && r.data?.stripe_checkout_url) {
        toast.info(`Verified intro fee ($${r.data.amount_usd}) &mdash; redirecting…`);
        window.location.assign(r.data.stripe_checkout_url);
        return;
      }
      toast.success(r.data?.message || "Sent.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send inquiry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intl-svc-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">
            Request a quote from {pro?.display_name}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            We&rsquo;ll forward your details to the pro. They reply directly to your email.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input placeholder="Your full name" value={form.buyer_name} onChange={set("buyer_name")} data-testid="intl-svc-quote-name" />
          <Input placeholder="Your email" type="email" value={form.buyer_email} onChange={set("buyer_email")} data-testid="intl-svc-quote-email" />
          <Input placeholder="Phone (optional)" value={form.buyer_phone} onChange={set("buyer_phone")} />
          <Input placeholder="Date / window" value={form.requested_date} onChange={set("requested_date")} />
          <Input placeholder="Your city" value={form.buyer_city} onChange={set("buyer_city")} />
          <Input placeholder="Your country" value={form.buyer_country} onChange={set("buyer_country")} />
        </div>
        <Textarea placeholder="Notes (dog breed, age, any details the pro needs)" rows={3} value={form.notes} onChange={set("notes")} className="mt-2" />
        <label className="flex gap-2 items-start mt-2 text-[11px] leading-relaxed text-[#5C5C5C]">
          <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="intl-svc-quote-ack" />
          <span>I understand Armstrong is a venue and is not party to the service. I&rsquo;ll verify credentials with the pro directly.</span>
        </label>
        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="rounded-full bg-crimson hover:bg-ink text-white"
            disabled={busy || !ack}
            onClick={submit}
            data-testid="intl-svc-quote-submit"
          >
            {busy ? "Sending…" : "Send inquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
